import { DataWindow, SortOrder, equalsShallow, stitchWindows } from '@/utils';

type StitchedEntry = {
    sources: readonly unknown[][];
    timestampKey: string;
    sortOrder: SortOrder;
    stitched: readonly unknown[];
};

/**
 * How many sets of window arrays one first array holds a stitched answer for.
 *
 * Two hooks whose ranges start together but end apart are handed the very same first chunk out of the
 * query cache while asking for different later windows. A slot per set is what lets both keep a stable
 * answer. It is well above the ranges one page shows at once.
 */
const STITCHED_SOURCES_PER_KEY = 4;

/** The answer for an empty window list, held so that path hands back one array like every other. */
const EMPTY_SERIES: never[] = [];

/**
 * Stitches a set of window arrays into one series and hands back the array stitched last while every
 * window behind it is unchanged.
 *
 * Entries are weakly keyed on the first window array's own object identity, so an answer goes when
 * react-query drops the rows behind it. One first array holds an entry per set of windows read under it,
 * up to {@link STITCHED_SOURCES_PER_KEY}, and reading an entry moves it to the end, so the set evicted is
 * the one least recently asked for. Two hooks alternating over the same first array therefore both keep
 * hitting.
 *
 * The identity of what {@link stitch} returns is what the draw effects downstream compare on. Every hook
 * asking for the same range is handed the very same window arrays out of the query cache, so keying on
 * one of them is what lets a single stitched array serve all of them: the historical data tab runs about
 * ten of these over one set of sensor windows, and each holding its own copy is a second copy of every row
 * on the page.
 */
export class StitchedSeriesCache {
    private readonly bySources = new WeakMap<object, StitchedEntry[]>();

    /**
     * Stitches `windows` into one series on `timestampKey`, in `sortOrder`.
     *
     * The settled chunks cannot gain data, so a live refetch returning the same items hands back the same
     * array: a fresh one would redraw every chart on the page for each push.
     *
     * The entry is narrowed back to `TItem` because it was stitched from these very arrays under this very
     * `timestampKey` and `sortOrder`, so it is this caller's series.
     */
    stitch<TItem extends Record<TTimestampKey, string>, TTimestampKey extends string>(
        windows: DataWindow<TItem>[],
        timestampKey: TTimestampKey,
        sortOrder: SortOrder
    ): TItem[] {
        // `sources[0]` is the weak key below, and a WeakMap rejects a non-object
        if (!windows.length) {
            return EMPTY_SERIES;
        }

        const sources = windows.map((window) => window.data);
        const entries = this.bySources.get(sources[0]) ?? [];
        /*
         * Matched on every source array, so a set of windows sharing only its first array reaches its own
         * entry rather than overwriting another set's.
         */
        const heldIndex = entries.findIndex(
            (entry) =>
                entry.timestampKey === timestampKey &&
                entry.sortOrder === sortOrder &&
                equalsShallow(entry.sources, sources)
        );

        if (heldIndex !== -1) {
            const [held] = entries.splice(heldIndex, 1);
            // reading counts as a use, so a set of windows still in rotation outlives one that is not
            entries.push(held);
            return held.stitched as TItem[];
        }

        const stitched = stitchWindows(windows, timestampKey, sortOrder);
        entries.push({ sources, timestampKey, sortOrder, stitched });
        if (entries.length > STITCHED_SOURCES_PER_KEY) {
            entries.shift();
        }
        this.bySources.set(sources[0], entries);

        return stitched;
    }
}

/** Shared, so every hook stitching the same windows is handed one array. */
export const stitchedSeriesCache = new StitchedSeriesCache();
